import type { LocaleCode } from '@/i18n/locales'
import { LOCALES } from '@/i18n/locales'
import type { PersonaId } from '@/persona/personas'
import type { Microphone } from '../audio/microphone'
import { SpeechEnvelope } from '../audio/level'
import type {
  ConnectOptions,
  ListenOptions,
  ListenResult,
  SpeakOptions,
  SpeakResult,
  VoiceCapabilities,
  VoiceEvent,
  VoiceListener,
  VoiceTransport,
} from '../types'
import { VoiceError } from '../types'
import type { SpeechRecognitionLike } from './webSpeechApi'
import {
  chunkForSpeech,
  estimateSpeechDurationMs,
  getSpeechRecognitionConstructor,
  getSpeechSynthesis,
  getUtteranceConstructor,
  isHalfDuplexPlatform,
  loadVoices,
  pickVoice,
} from './webSpeechApi'

/**
 * The browser's own speech stack, wrapped so it behaves.
 *
 * This is a development and fallback transport, not the destination. Namu's
 * own models will replace it. But it is what makes the product real today, and
 * every workaround in here is for a specific documented failure:
 *
 *  - `onend` frequently never fires on iOS, so every await has a deadline.
 *  - A cancelled utterance fires `end` too, so completion is tracked explicitly
 *    rather than inferred from the event.
 *  - Chrome's remote voices stop around 15 seconds with no event at all, so
 *    text is chunked and device-local voices are preferred.
 *  - `continuous` recognition is unreliable everywhere and does nothing at all
 *    on Chrome Android, so end-of-turn is timed here rather than delegated.
 *  - Chrome's result list grows forever, so results are read from
 *    `event.resultIndex`.
 */

export interface WebSpeechTransportOptions {
  /** Shared microphone. The transport meters through it rather than opening its own. */
  microphone?: Microphone
}

const DEFAULT_START_TIMEOUT_MS = 9000
const DEFAULT_END_SILENCE_MS = 900
const DEFAULT_MAX_TURN_MS = 30000
/** How long to hold a turn open for someone typing rather than speaking. */
const TYPED_WAIT_MS = 300000
/** Slack added to the estimated utterance length before giving up on it. */
const SPEECH_TIMEOUT_SLACK_MS = 4000

export class WebSpeechTransport implements VoiceTransport {
  readonly id = 'web-speech'

  private readonly listeners = new Set<VoiceListener>()
  private readonly envelope = new SpeechEnvelope()
  private readonly microphone: Microphone | undefined
  private unsubscribeMic: (() => void) | null = null

  private locale: LocaleCode = 'en'
  private rate = 1
  private muted = false
  private voice: SpeechSynthesisVoice | null = null
  private voices: SpeechSynthesisVoice[] = []

  private recognition: SpeechRecognitionLike | null = null
  private activeListen: ActiveListen | null = null
  /**
   * An answer typed or tapped before the assistant was ready to hear it.
   * People answer while the question is still being spoken; throwing that away
   * makes a visible, enabled control silently do nothing.
   */
  private pendingAnswer: string | null = null
  /**
   * Set once the recogniser has refused for good: permission denied, no
   * capture device, or the service turned off. After that, listening means
   * waiting for typed input, because retrying a recogniser that has said no
   * produces an instant error that the conversation loop cannot tell apart
   * from the person hanging up.
   */
  private recognitionUnavailable = false
  private speechCancelled = false
  private outputTimer: ReturnType<typeof setInterval> | null = null

  constructor(options: WebSpeechTransportOptions = {}) {
    this.microphone = options.microphone
  }

  get capabilities(): VoiceCapabilities {
    const descriptor = LOCALES[this.locale]
    const hasRecognition = getSpeechRecognitionConstructor() !== null
    const hasSynthesis = getSpeechSynthesis() !== null
    // A voice for *this* language, not just any voice. Speaking Hausa with an
    // English voice is a mispronunciation, not a graceful degradation.
    const hasLocaleVoice = this.voice !== null
    const fullDuplex = !isHalfDuplexPlatform()

    return {
      recognition: hasRecognition,
      synthesis:
        hasSynthesis && (hasLocaleVoice || descriptor.noVoiceStrategy === 'fallback-voice'),
      inputLevel: this.microphone !== undefined,
      fullDuplex,
      /**
       * False, and it must stay false until barge-in is actually built.
       *
       * This flag is what puts "Talk to interrupt" on screen during every
       * utterance. Reporting it true while `speak()` never listens meant the
       * interface instructed people to do something that did nothing: they
       * talked, Namu talked over them, every word was lost, and the turn that
       * opened afterwards timed out on someone who had already finished. The
       * browser stack here is half duplex in practice; promising otherwise is
       * worse than not offering it.
       */
      bargeIn: false,
      endpointing: hasRecognition,
      duplex: false,
    }
  }

  // -- lifecycle ------------------------------------------------------------

  async connect(options: ConnectOptions): Promise<void> {
    this.locale = options.locale
    this.rate = options.rate ?? 1
    this.emit({ type: 'connection', status: 'connecting' })

    if (!getSpeechRecognitionConstructor() && !getSpeechSynthesis()) {
      const error = new VoiceError('unsupported', 'This browser has no speech support.', {
        recoverable: false,
      })
      this.emit({ type: 'error', error })
      throw error
    }

    this.voices = await loadVoices()
    this.voice = pickVoice(this.voices, LOCALES[this.locale].speechTags)

    if (!this.voice && LOCALES[this.locale].noVoiceStrategy === 'caption') {
      // Not fatal. The session runs with captions carrying the assistant's
      // words, which is the honest behaviour for a language with no voice.
      this.emit({
        type: 'error',
        error: new VoiceError('no-voice', `No ${LOCALES[this.locale].englishName} voice installed.`),
      })
    }

    if (this.microphone) {
      this.unsubscribeMic?.()
      this.unsubscribeMic = this.microphone.subscribe((level) => {
        this.emit({ type: 'level', source: 'user', value: level })
      })
    }

    this.emit({ type: 'connection', status: 'open' })
  }

  async disconnect(): Promise<void> {
    this.cancelSpeech()
    this.teardownRecognition('aborted')
    this.unsubscribeMic?.()
    this.unsubscribeMic = null
    this.emit({ type: 'connection', status: 'closed' })
  }

  // -- speaking -------------------------------------------------------------

  async speak(text: string, options: SpeakOptions = {}): Promise<SpeakResult> {
    const synthesis = getSpeechSynthesis()
    const UtteranceCtor = getUtteranceConstructor()
    const startedAt = Date.now()

    // The caption goes out first and unconditionally. If synthesis fails or
    // there is no voice for this language, the user still gets the words.
    this.emit({ type: 'transcript', speaker: 'namu', text, final: true })

    if (!synthesis || !UtteranceCtor || (!this.voice && LOCALES[this.locale].noVoiceStrategy === 'caption')) {
      // Caption-only: hold for a readable beat so the conversation still has
      // rhythm, and so the caller's turn-taking logic is unchanged.
      const readMs = estimateSpeechDurationMs(text, this.rate)
      this.emit({ type: 'speech_start', speaker: 'namu' })
      const completed = await this.holdFor(readMs, options.signal)
      this.emit({ type: 'speech_end', speaker: 'namu', interrupted: !completed })
      return { completed, durationMs: Date.now() - startedAt }
    }

    this.speechCancelled = false
    this.emit({ type: 'speech_start', speaker: 'namu' })
    this.envelope.start(Date.now())
    this.startOutputLevels()

    const chunks = chunkForSpeech(text)
    let completed = true

    for (const chunk of chunks) {
      if (this.speechCancelled || options.signal?.aborted) {
        completed = false
        break
      }
      const spoken = await this.speakChunk(synthesis, UtteranceCtor, chunk)
      if (!spoken) {
        completed = false
        break
      }
    }

    this.stopOutputLevels()
    this.envelope.stop()
    this.emit({ type: 'speech_end', speaker: 'namu', interrupted: !completed })
    return { completed, durationMs: Date.now() - startedAt }
  }

  private speakChunk(
    synthesis: SpeechSynthesis,
    UtteranceCtor: typeof SpeechSynthesisUtterance,
    chunk: string,
  ): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const utterance = new UtteranceCtor(chunk)
      utterance.lang = this.voice?.lang ?? LOCALES[this.locale].bcp47
      if (this.voice) utterance.voice = this.voice
      utterance.rate = this.rate

      let settled = false
      const finish = (ok: boolean) => {
        if (settled) return
        settled = true
        clearTimeout(deadline)
        resolve(ok)
      }

      utterance.onboundary = (event) => {
        const wordLength = 'charLength' in event ? Number(event.charLength) || 4 : 4
        this.envelope.onWord(wordLength, Date.now())
      }
      // `end` fires for a cancelled utterance too, so completion is read from
      // the cancellation flag rather than trusted from the event.
      utterance.onend = () => finish(!this.speechCancelled)
      utterance.onerror = () => finish(false)

      // iOS often never fires `end` at all. Without this the whole conversation
      // stops on a promise that will never settle.
      const deadline = setTimeout(
        () => {
          try {
            synthesis.cancel()
          } catch {
            // Cancelling an already-finished queue is not an error worth raising.
          }
          finish(!this.speechCancelled)
        },
        estimateSpeechDurationMs(chunk, this.rate) + SPEECH_TIMEOUT_SLACK_MS,
      )

      try {
        synthesis.speak(utterance)
      } catch (error) {
        this.emit({
          type: 'error',
          error: new VoiceError('synthesis-failed', 'The voice could not start.', { cause: error }),
        })
        finish(false)
      }
    })
  }

  cancelSpeech(): void {
    this.speechCancelled = true
    const synthesis = getSpeechSynthesis()
    try {
      // `cancel` rather than `pause`: on Android `pause` behaves like `cancel`
      // anyway, and a paused queue blocks every later utterance.
      synthesis?.cancel()
    } catch {
      // Nothing to cancel.
    }
    this.stopOutputLevels()
    this.envelope.stop()
  }

  private startOutputLevels(): void {
    this.stopOutputLevels()
    this.outputTimer = setInterval(() => {
      this.emit({ type: 'level', source: 'namu', value: this.envelope.sample(Date.now()) })
    }, 50)
  }

  private stopOutputLevels(): void {
    if (this.outputTimer !== null) {
      clearInterval(this.outputTimer)
      this.outputTimer = null
    }
    this.emit({ type: 'level', source: 'namu', value: 0 })
  }

  private holdFor(ms: number, signal?: AbortSignal): Promise<boolean> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve(true), ms)
      signal?.addEventListener(
        'abort',
        () => {
          clearTimeout(timer)
          resolve(false)
        },
        { once: true },
      )
    })
  }

  // -- listening ------------------------------------------------------------

  listen(options: ListenOptions = {}): Promise<ListenResult> {
    // An answer given before the turn opened counts for this turn.
    if (this.pendingAnswer !== null) {
      const transcript = this.pendingAnswer
      this.pendingAnswer = null
      return Promise.resolve({ status: 'typed', transcript })
    }

    const RecognitionCtor = getSpeechRecognitionConstructor()
    if (!RecognitionCtor || this.recognitionUnavailable) {
      // No recogniser, or one that has refused. The typed path is the only way
      // in, and it is a first-class path rather than an error. The wait is long
      // because the person is typing rather than talking, and hurrying them
      // into a timeout would abandon the question they are answering.
      return new Promise<ListenResult>((resolve) => {
        const typedOptions: ListenOptions = { ...options, startTimeoutMs: TYPED_WAIT_MS }
        const active = new ActiveListen(resolve, typedOptions)
        this.activeListen = active
        active.armStartTimeout(() => this.settleListen({ status: 'timeout' }))
        options.signal?.addEventListener('abort', () => this.settleListen({ status: 'aborted' }), {
          once: true,
        })
      })
    }

    this.teardownRecognition('aborted')

    return new Promise<ListenResult>((resolve) => {
      const active = new ActiveListen(resolve, options)
      this.activeListen = active

      const recognition = new RecognitionCtor()
      this.recognition = recognition
      recognition.lang = LOCALES[this.locale].bcp47
      recognition.interimResults = true
      recognition.maxAlternatives = 1
      // Kept on where it works, but never depended on: end-of-turn is decided
      // by the silence timer below.
      recognition.continuous = true

      recognition.onspeechstart = () => {
        if (this.activeListen !== active) return
        active.clearStartTimeout()
        active.markSpeechStarted()
        this.emit({ type: 'speech_start', speaker: 'user' })
      }

      recognition.onresult = (event) => {
        if (this.activeListen !== active) return
        active.clearStartTimeout()

        // From resultIndex, not zero: Chrome's list keeps every earlier result.
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const result = event.results[i]
          if (!result) continue
          const alternative = result[0]
          if (!alternative) continue

          if (result.isFinal) {
            active.appendFinal(alternative.transcript, alternative.confidence)
          } else {
            active.setInterim(alternative.transcript)
          }
        }

        const text = active.currentText()
        if (text.length > 0) {
          this.emit({ type: 'transcript', speaker: 'user', text, final: false })
          active.markSpeechStarted()
          this.emit({ type: 'speech_start', speaker: 'user' })
        }

        active.armSilenceTimeout(() => this.finishListen())
      }

      recognition.onerror = (event) => {
        if (this.activeListen !== active) return
        switch (event.error) {
          case 'no-speech':
            // Benign and common. Treated as a quiet turn, not a failure.
            this.settleListen({ status: 'timeout' })
            break
          case 'aborted':
            this.settleListen({ status: 'aborted' })
            break
          case 'not-allowed':
          case 'service-not-allowed':
            this.recognitionUnavailable = true
            this.emit({
              type: 'error',
              error: new VoiceError('mic-denied', 'Speech recognition was refused.', {
                recoverable: false,
              }),
            })
            // Not `aborted`. A refused recogniser is not the person hanging up,
            // and treating it as one made the whole interview fire six
            // questions in fifteen seconds with nothing to answer them.
            this.settleListen({ status: 'timeout' })
            break
          case 'audio-capture':
            this.recognitionUnavailable = true
            this.emit({
              type: 'error',
              error: new VoiceError('mic-missing', 'No microphone was available to listen with.'),
            })
            this.settleListen({ status: 'timeout' })
            break
          case 'network':
            this.emit({
              type: 'error',
              error: new VoiceError('network', 'Speech recognition lost its connection.'),
            })
            this.settleListen({ status: 'aborted' })
            break
          default:
            this.settleListen({ status: 'unintelligible' })
        }
      }

      recognition.onend = () => {
        if (this.activeListen !== active) return
        // The recogniser can stop on its own, mid-turn, on several platforms.
        // Whatever was heard counts.
        this.finishListen()
      }

      active.armStartTimeout(() => this.settleListen({ status: 'timeout' }))
      active.armMaxTurnTimeout(() => this.finishListen())

      if (options.signal) {
        options.signal.addEventListener('abort', () => this.settleListen({ status: 'aborted' }), {
          once: true,
        })
      }

      try {
        recognition.start()
      } catch (error) {
        this.emit({
          type: 'error',
          error: new VoiceError('recognition-failed', 'Listening could not start.', { cause: error }),
        })
        this.settleListen({ status: 'aborted' })
      }
    })
  }

  /** Ends the turn, keeping whatever has been heard so far. */
  endTurn(): void {
    this.finishListen()
  }

  private finishListen(): void {
    const active = this.activeListen
    if (!active) return

    const transcript = active.finalText().trim()
    if (transcript.length === 0) {
      this.settleListen(active.heardSpeech() ? { status: 'unintelligible' } : { status: 'timeout' })
      return
    }

    this.emit({ type: 'transcript', speaker: 'user', text: transcript, final: true })
    this.settleListen({
      status: 'final',
      transcript,
      confidence: active.confidence(),
      durationMs: active.elapsed(),
    })
  }

  private settleListen(result: ListenResult): void {
    const active = this.activeListen
    if (!active) return
    this.activeListen = null
    active.dispose()

    // Every `speech_start` gets its `speech_end`, whatever the outcome.
    //
    // This used to fire only when a transcript came back. A turn where the
    // recogniser heard someone but produced no words left the machine stuck in
    // `user_speaking`, where the assistant's own speech event is illegal and
    // gets rejected, so the status line and the screen-reader status region
    // both insisted the user was still talking through the whole of Namu's
    // reply. Unbalanced events are how a state machine drifts away from reality.
    if (active.heardSpeech()) {
      this.emit({ type: 'speech_end', speaker: 'user', interrupted: result.status === 'aborted' })
    }

    this.teardownRecognition(result.status === 'aborted' ? 'aborted' : 'stopped')
    active.resolve(result)
  }

  private teardownRecognition(reason: 'aborted' | 'stopped'): void {
    const recognition = this.recognition
    if (!recognition) return
    this.recognition = null
    recognition.onresult = null
    recognition.onerror = null
    recognition.onend = null
    recognition.onspeechstart = null
    recognition.onspeechend = null
    recognition.onstart = null
    try {
      if (reason === 'aborted') recognition.abort()
      else recognition.stop()
    } catch {
      // Stopping a recogniser that never started throws on some platforms.
    }
  }

  submitTyped(text: string): void {
    this.emit({ type: 'transcript', speaker: 'user', text, final: true })
    if (this.activeListen) {
      this.settleListen({ status: 'typed', transcript: text })
      return
    }
    this.pendingAnswer = text
  }

  // -- settings -------------------------------------------------------------

  setMuted(muted: boolean): void {
    this.muted = muted
    this.microphone?.setMuted(muted)
    if (!muted) return

    // Muting must actually stop the recogniser, not just the meter: a muted
    // track still feeds Chrome's recogniser on some platforms.
    //
    // But tearing the recogniser down under an open turn left the caller
    // waiting on a listen that nothing could ever settle. Unmuting did not
    // rebuild it, the status label dropped back to "Listening" the moment mute
    // came off, and the person talked into a dead session until the thirty
    // second ceiling. Ending the turn hands control back to the conversation
    // loop, which opens a fresh one.
    this.emit({ type: 'level', source: 'user', value: 0 })
    if (this.activeListen) this.settleListen({ status: 'aborted' })
    else this.teardownRecognition('aborted')
  }

  get isMuted(): boolean {
    return this.muted
  }

  setLocale(locale: LocaleCode): void {
    if (locale === this.locale) return
    this.locale = locale
    this.voice = pickVoice(this.voices, LOCALES[locale].speechTags)
    if (this.recognition) this.recognition.lang = LOCALES[locale].bcp47
  }

  setPersona(_persona: PersonaId): void {
    // Nothing to honour. Nadia and Salim are voices on Namu's synthesiser, and
    // this transport speaks with whatever the browser and the operating system
    // installed. Picking a browser voice by the persona's name would sell a
    // choice the device cannot deliver, so the fallback stays plainly itself.
  }

  setRate(rate: number): void {
    this.rate = Math.min(2, Math.max(0.5, rate))
  }

  /** The voice currently selected, or null when captioning instead. */
  get activeVoice(): SpeechSynthesisVoice | null {
    return this.voice
  }

  subscribe(listener: VoiceListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private emit(event: VoiceEvent): void {
    for (const listener of [...this.listeners]) listener(event)
  }
}

/**
 * Bookkeeping for one listening turn.
 *
 * Pulled out of the transport because turn accounting is where voice UIs
 * usually go wrong: partial results that never finalise, a second `speech_start`
 * for the same turn, timers left armed after the turn resolved.
 */
class ActiveListen {
  private readonly finals: string[] = []
  private interim = ''
  private confidences: number[] = []
  private speechStarted = false
  private startedAt = Date.now()
  private startTimer: ReturnType<typeof setTimeout> | null = null
  private silenceTimer: ReturnType<typeof setTimeout> | null = null
  private maxTurnTimer: ReturnType<typeof setTimeout> | null = null

  readonly resolve: (result: ListenResult) => void
  private readonly options: ListenOptions

  constructor(resolve: (result: ListenResult) => void, options: ListenOptions) {
    this.resolve = resolve
    this.options = options
  }

  armStartTimeout(onTimeout: () => void): void {
    this.clearStartTimeout()
    this.startTimer = setTimeout(onTimeout, this.options.startTimeoutMs ?? DEFAULT_START_TIMEOUT_MS)
  }

  clearStartTimeout(): void {
    if (this.startTimer !== null) {
      clearTimeout(this.startTimer)
      this.startTimer = null
    }
  }

  armSilenceTimeout(onSilence: () => void): void {
    if (this.silenceTimer !== null) clearTimeout(this.silenceTimer)
    this.silenceTimer = setTimeout(onSilence, this.options.endSilenceMs ?? DEFAULT_END_SILENCE_MS)
  }

  armMaxTurnTimeout(onMax: () => void): void {
    this.maxTurnTimer = setTimeout(onMax, this.options.maxTurnMs ?? DEFAULT_MAX_TURN_MS)
  }

  markSpeechStarted(): void {
    this.speechStarted = true
  }

  heardSpeech(): boolean {
    return this.speechStarted
  }

  appendFinal(text: string, confidence: number): void {
    const trimmed = text.trim()
    if (trimmed.length === 0) return
    this.finals.push(trimmed)
    if (Number.isFinite(confidence) && confidence > 0) this.confidences.push(confidence)
    this.interim = ''
  }

  setInterim(text: string): void {
    this.interim = text.trim()
  }

  /** Everything heard so far, including the unfinalised tail. */
  currentText(): string {
    return [...this.finals, this.interim].filter(Boolean).join(' ').trim()
  }

  /**
   * The answer. Falls back to the interim text when nothing ever finalised,
   * which happens routinely on Safari and would otherwise throw away a
   * perfectly good answer.
   */
  finalText(): string {
    if (this.finals.length > 0) return this.finals.join(' ')
    return this.interim
  }

  confidence(): number {
    if (this.confidences.length === 0) {
      // No confidence reported. Interim-only text is a weaker signal, so it is
      // scored low enough to be read back for confirmation.
      return this.finals.length > 0 ? 0.8 : 0.55
    }
    const total = this.confidences.reduce((sum, value) => sum + value, 0)
    return total / this.confidences.length
  }

  elapsed(): number {
    return Date.now() - this.startedAt
  }

  dispose(): void {
    this.clearStartTimeout()
    if (this.silenceTimer !== null) clearTimeout(this.silenceTimer)
    if (this.maxTurnTimer !== null) clearTimeout(this.maxTurnTimer)
    this.silenceTimer = null
    this.maxTurnTimer = null
  }
}
